import { Project } from '@/types/projects'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'
import { createDetail, deleteCall } from '../request-wrappers'

const service = buildService<Project>({
  pathCategory: 'projects',
})

export function useGetProject({
  id,
  ...options
}: UseGetDetailOptions<Project>) {
  const query = useGetDetail<Project>({
    id,
    queryKey: queryKeys.projects.detail({
      id,
      filters: options.filters,
      persist: true,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    project: query.data,
    ...query,
  }
}

export function useCreateProject(
  options: UseCreateDetailOptions<Project> = {},
) {
  const mutation = useCreateDetail<Project>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createProject: mutation.mutate,
    createProjectAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateProject(
  options: UseUpdateDetailOptions<Project> = {},
) {
  const mutation = useUpdateDetail<Project>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateProject: mutation.mutate,
    updateProjectAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteProject(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<Project>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteProject: mutation.mutate,
    deleteProjectAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetProjects({
  filters,
  ...options
}: UseGetListOptions<Project> = {}) {
  const query = useGetList<Project>({
    filters,
    queryKey: queryKeys.projects.list({ filters, persist: true }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    projects: query.allData,
    ...query,
  }
}

export function useUpdateProjects(options: UseUpdateListOptions<Project> = {}) {
  const mutation = useUpdateList<Project>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateProjects: mutation.mutate,
    updateProjectsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteProjects({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<Project>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteProjects: mutation.mutate,
    deleteProjectsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Project with Defaults
 * Creates a new Project with default values.
 */
export function useCreateProjectWithDefaults(
  options: UseCreateDetailOptions<Omit<Project, 'namespace_id'>, Project> = {},
) {
  const mutation = useCreateDetail<Omit<Project, 'namespace_id'>, Project>({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/pd/projects/create-with-defaults',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    createProject: mutation.mutate,
    createProjectAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Delete Project with Defaults
 * Used to delete a Project with default values (namespaces).
 */
export function useDeleteProjectWithDefaults(
  options: UseDeleteDetailOptions = {},
) {
  const mutation = useDeleteDetail<Project>({
    serviceFn: ({ id, filters, axiosOptions }) => {
      return deleteCall({
        url: `/v2/pd/projects/${id}/delete-with-defaults`,
        filters,
        axiosOptions,
      })
    },
    ...options,
  })

  return {
    deleteProject: mutation.mutate,
    deleteProjectAsync: mutation.mutateAsync,
    ...mutation,
  }
}
